// useVersionCheck — detects when a new build has been deployed and prompts
// the user to reload via a toast. Reload is always user-initiated; we never
// silently reload behind a backgrounded tab because doing so disconnects
// the squawk audio and Chrome's autoplay policy won't let it auto-resume
// on low-MEI origins like staging/dev.
//
// Relies on /version.json being emitted at build time (see vite.config.js) and
// served with Cache-Control: no-cache by Caddy so polls always see the latest SHA.
// Complements src/stale-chunk-handler.ts (reactive reload on chunk 404) with a
// proactive deploy-detection mechanism.

import { useEffect, useRef } from "react";
import toast from "react-hot-toast";
import { useVersionState } from "./useVersionState";

const TOAST_ID = "version-update";

export function useVersionCheck() {
  const { isUpdateAvailable } = useVersionState();
  const toastShownRef = useRef(false);

  useEffect(() => {
    if (!isUpdateAvailable || toastShownRef.current) return;

    toastShownRef.current = true;

    console.info("[version-check] new build available");

    toast(
      (t) => (
        <span className="flex items-center gap-2">
          <span>New version available.</span>
          <button
            onClick={() => {
              toast.dismiss(t.id);
              window.location.reload();
            }}
            className="px-2 py-1 rounded bg-white/20 hover:bg-white/30 text-white font-medium"
          >
            Reload
          </button>
        </span>
      ),
      {
        id: TOAST_ID,
        duration: Infinity,
        position: "bottom-right",
      }
    );
  }, [isUpdateAvailable]);
}
